import { Copy, Link2, Share2 } from "lucide-react";
import {
  useCreate,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/atomic-crm/misc/ResponsiveDialog";
import { Input } from "@/components/ui/input";

import type { ShareLink } from "../types";

export const shareUrl = (token: string) =>
  `${window.location.origin}${window.location.pathname}#/p/${token}`;

/** Creates, copies and revokes the public (read-only) link of a deal's progress or a style. */
export const ShareLinkButton = ({
  kind,
  dealId,
  styleId,
}: {
  kind: ShareLink["kind"];
  dealId?: Identifier;
  styleId?: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [create, { isPending }] = useCreate<ShareLink>();
  const [update] = useUpdate<ShareLink>();
  const filter =
    kind === "deal_progress"
      ? { kind, deal_id: dealId, revoked: false }
      : { kind, style_id: styleId, revoked: false };
  const { data: links, refetch } = useGetList<ShareLink>(
    "share_links",
    { filter, pagination: { page: 1, perPage: 10 } },
    { enabled: open },
  );
  const link = links?.[0];

  const copy = async (token: string) => {
    await navigator.clipboard?.writeText(shareUrl(token)).catch(() => {});
  };

  const createLink = () =>
    create(
      "share_links",
      {
        data: {
          kind,
          deal_id: dealId ?? null,
          style_id: styleId ?? null,
        } as Partial<ShareLink>,
      },
      {
        returnPromise: false,
        onSuccess: async (created) => {
          await copy(created.token);
          notify("hub.share.created");
          refetch();
        },
      },
    );

  const revoke = (target: ShareLink) =>
    update(
      "share_links",
      { id: target.id, data: { revoked: true }, previousData: target },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("hub.share.revoked");
          refetch();
        },
      },
    );

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Share2 className="size-4" />
        {translate(
          kind === "deal_progress" ? "hub.delivery.share" : "hub.styles.share",
        )}
      </Button>
      <ResponsiveDialog open={open} onOpenChange={setOpen}>
        <ResponsiveDialogContent>
          <DialogHeader>
            <DialogTitle>{translate("hub.share.title")}</DialogTitle>
            <DialogDescription>{translate("hub.share.hint")}</DialogDescription>
          </DialogHeader>
          {link ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input readOnly value={shareUrl(link.token)} />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    copy(link.token);
                    notify("hub.common.copied");
                  }}
                  aria-label={translate("hub.common.copy")}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              <div className="flex justify-between">
                <a
                  href={shareUrl(link.token)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm hover:underline"
                >
                  <Link2 className="size-4" />
                  {translate("hub.common.open")}
                </a>
                <Button
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => revoke(link)}
                >
                  {translate("hub.share.revoke")}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={createLink} disabled={isPending}>
              {translate("hub.share.create")}
            </Button>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
};
